import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faSignOutAlt, faUserCheck } from '@fortawesome/free-solid-svg-icons';

interface TimeoutModalProps {
  isOpen: boolean;
  onStayLoggedIn: () => void;
  onLogout: () => void;
  countdownSeconds: number;
}

const TimeoutModal: React.FC<TimeoutModalProps> = ({
  isOpen,
  onStayLoggedIn,
  onLogout,
  countdownSeconds
}) => {
  const [timeLeft, setTimeLeft] = useState(countdownSeconds);

  useEffect(() => {
    if (!isOpen) {
      setTimeLeft(countdownSeconds);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, countdownSeconds, onLogout]);

  if (!isOpen) return null;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = () => {
    return ((countdownSeconds - timeLeft) / countdownSeconds) * 100;
  };

  const getProgressColor = () => {
    const percentage = getProgressPercentage();
    if (percentage > 80) return 'progress-error';
    if (percentage > 60) return 'progress-warning';
    return 'progress-info';
  };

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-md">
        <div className="flex items-center gap-3 mb-4">
          <FontAwesomeIcon icon={faClock} className="text-warning text-xl" />
          <h3 className="font-bold text-lg">Session Timeout</h3>
        </div>
        
        <div className="text-center mb-6">
          <p className="text-base-content mb-4">
            Your session will expire due to inactivity. You will be automatically logged out in:
          </p>
          
          <div className="text-4xl font-bold text-warning mb-4">
            {formatTime(timeLeft)}
          </div>
          
          <div className="w-full bg-base-300 rounded-full h-2 mb-4">
            <div 
              className={`h-2 rounded-full transition-all duration-1000 ${getProgressColor()}`}
              style={{ width: `${getProgressPercentage()}%` }}
            ></div>
          </div>
        </div>

        <div className="modal-action gap-3">
          <button 
            className="btn btn-outline btn-error gap-2"
            onClick={onLogout}
          >
            <FontAwesomeIcon icon={faSignOutAlt} />
            Logout Now
          </button>
          
          <button 
            className="btn btn-primary gap-2"
            onClick={onStayLoggedIn}
          >
            <FontAwesomeIcon icon={faUserCheck} />
            Stay Logged In
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimeoutModal; 